import type { ToolCallRecord } from '@k4ia/shared'
import { useT } from '../lib/i18n'

/**
 * Was K4ia per `show_media` zeigt: ein Bild, ein Video oder ein PDF aus dem
 * Admin, unter der Antwort in der Blase. Die Adresse kommt aus dem Ergebnis
 * des Werkzeugs (`/media/…`, routes/media.ts im API-Worker); die
 * Bildunterschrift aus dem Aufruf.
 *
 * Bis hierher landeten die Ergebnisse nur in den Daten, nie auf dem Schirm —
 * jedes im Admin hochgeladene Bild war für Besucher unsichtbar.
 */
export function medienAus(toolCalls: ToolCallRecord[]): Medium[] {
  const liste: Medium[] = []
  for (const t of toolCalls) {
    if (t.name !== 'show_media' || !t.output) continue
    const url = t.output['url']
    const type = t.output['type']
    if (typeof url !== 'string' || !url || typeof type !== 'string') continue
    // Nur unsere eigenen Adressen: Das Ergebnis stammt aus der Wissensbasis,
    // aber ein Bild von fremder Adresse gehört trotzdem nicht in die Blase.
    if (!/^(\/|https?:\/\/)/.test(url) || !['image', 'video', 'pdf'].includes(type)) continue
    const caption = t.input['caption']
    liste.push({
      id: t.id,
      url,
      type: type as Medium['type'],
      caption: typeof caption === 'string' ? caption.trim() : '',
    })
  }
  return liste
}

export interface Medium {
  id: string
  url: string
  type: 'image' | 'video' | 'pdf'
  caption: string
}

export function MediaCard({ medium }: { medium: Medium }) {
  const t = useT()
  if (medium.type === 'image') {
    return (
      <figure className="mt-3" data-testid="media-card">
        <img
          src={medium.url}
          alt={medium.caption || t.mediaFallback}
          loading="lazy"
          className="max-h-80 w-full rounded-2xl object-contain"
        />
        {medium.caption && (
          <figcaption className="mt-1 text-xs text-text-muted">{medium.caption}</figcaption>
        )}
      </figure>
    )
  }
  if (medium.type === 'video') {
    return (
      <figure className="mt-3" data-testid="media-card">
        <video
          src={medium.url}
          controls
          playsInline
          preload="metadata"
          className="max-h-80 w-full rounded-2xl"
          aria-label={medium.caption || t.mediaFallback}
        />
        {medium.caption && (
          <figcaption className="mt-1 text-xs text-text-muted">{medium.caption}</figcaption>
        )}
      </figure>
    )
  }
  return (
    <a
      href={medium.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="media-card"
      className="mt-3 flex items-center gap-3 rounded-2xl bg-surface-2 px-4 py-3 text-sm font-semibold hover:text-brand"
    >
      <PdfIcon />
      <span className="min-w-0 flex-1 truncate">{medium.caption || t.openPdf}</span>
      <span className="shrink-0 text-xs text-text-muted">PDF</span>
    </a>
  )
}

function PdfIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm7 0v5h5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  )
}
